import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { ProgressSpinner } from "primereact/progressspinner";
import { fetchTickets } from "../../reducers/ticketSlice";
import { useLayout } from "../../context/LayoutContext";
import { assignSupportTicket } from "../../api/ticketAssignment.service";
import AssignmentControl from "../../components/Tickets/AssignmentControl";
import {
  normalizeAssignedTo,
  TICKET_AGENT_OPTIONS,
} from "../../constants/ticketAgents";
import "./tickets.css";

const statusOptions = ["Open", "In Progress", "Resolved", "Closed"];
const priorityOptions = ["Low", "Medium", "High", "Urgent"];

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return date.toLocaleString();
};

export const Tickets = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toastMessage } = useLayout();
  const { list, loading } = useSelector((state) => state.tickets);

  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    assignedTo: "",
    from: null,
    to: null,
    q: "",
    page: 0,
    limit: 10,
  });
  const [assignDrafts, setAssignDrafts] = useState({});
  const [assignLoadingById, setAssignLoadingById] = useState({});
  const [assignErrorById, setAssignErrorById] = useState({});

  const buildPayload = () => {
    const toDate = filters.to
      ? new Date(filters.to.getTime() - filters.to.getTimezoneOffset() * 60000)
          .toISOString()
          .substring(0, 10)
      : null;
    const fromDate = filters.from
      ? new Date(filters.from.getTime() - filters.from.getTimezoneOffset() * 60000)
          .toISOString()
          .substring(0, 10)
      : null;

    const hasFilters =
      Boolean(filters.status) ||
      Boolean(filters.priority) ||
      Boolean(filters.assignedTo) ||
      Boolean(fromDate) ||
      Boolean(toDate) ||
      Boolean(filters.q);

    if (!hasFilters) return {};

    return {
      status: filters.status || undefined,
      priority: filters.priority || undefined,
      assignedTo: normalizeAssignedTo(filters.assignedTo) || undefined,
      from: fromDate || undefined,
      to: toDate || undefined,
      page: filters.page,
      limit: filters.limit,
      q: filters.q || undefined,
    };
  };

  const refreshTickets = () => {
    dispatch(fetchTickets(buildPayload()));
  };

  const getErrorMessage = (error) =>
    error?.response?.data?.message || error?.message || "Unable to update assignment";

  useEffect(() => {
    refreshTickets();
  }, [
    filters.status,
    filters.priority,
    filters.assignedTo,
    filters.from,
    filters.to,
    filters.q,
    filters.page,
    filters.limit,
  ]);

  const onPage = (event) => {
    setFilters((prev) => ({
      ...prev,
      page: event.page,
      limit: event.rows,
    }));
  };

  const assignedBody = (row) => {
    const draftValue =
      assignDrafts[row.id] !== undefined
        ? assignDrafts[row.id]
        : normalizeAssignedTo(row.assignedTo);

    const setRowLoading = (value) => {
      setAssignLoadingById((prev) => ({ ...prev, [row.id]: value }));
    };

    const clearRowError = () => {
      setAssignErrorById((prev) => ({ ...prev, [row.id]: "" }));
    };

    const handleAssign = async () => {
      try {
        setRowLoading(true);
        clearRowError();
        await assignSupportTicket(row.id, draftValue);
        toastMessage({ severity: "success", detail: "Ticket assigned successfully" });
        refreshTickets();
      } catch (error) {
        const message = getErrorMessage(error);
        setAssignErrorById((prev) => ({ ...prev, [row.id]: message }));
        toastMessage({ severity: "error", detail: message });
      } finally {
        setRowLoading(false);
      }
    };

    const handleUnassign = async () => {
      try {
        setRowLoading(true);
        clearRowError();
        await assignSupportTicket(row.id, null);
        setAssignDrafts((prev) => ({ ...prev, [row.id]: "" }));
        toastMessage({ severity: "success", detail: "Ticket unassigned successfully" });
        refreshTickets();
      } catch (error) {
        const message = getErrorMessage(error);
        setAssignErrorById((prev) => ({ ...prev, [row.id]: message }));
        toastMessage({ severity: "error", detail: message });
      } finally {
        setRowLoading(false);
      }
    };

    return (
      <AssignmentControl
        value={draftValue}
        agentOptions={TICKET_AGENT_OPTIONS}
        loading={Boolean(assignLoadingById[row.id])}
        error={assignErrorById[row.id]}
        onChange={(value) =>
          setAssignDrafts((prev) => ({
            ...prev,
            [row.id]: value || "",
          }))
        }
        onAssign={handleAssign}
        onUnassign={handleUnassign}
        className="assignment-control--compact"
      />
    );
  };

  const viewBody = (row) => (
    <Button
      label="View"
      onClick={() => navigate(`/tickets/${row.id}`, { state: { ticket: row } })}
    />
  );

  const statusTag = (row) => {
    const key = (row.status || "open").toLowerCase().replace(" ", "-");
    return <span className={`ticket-badge status-${key}`}>{row.status || "-"}</span>;
  };

  const priorityTag = (row) => {
    const key = (row.priority || "low").toLowerCase();
    return <span className={`ticket-badge priority-${key}`}>{row.priority || "-"}</span>;
  };

  return (
    <div className="ticket-page">
      {loading.list && (
        <div className="loading-container">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
        </div>
      )}
      <div className="ticket-header">
        <div className="ticket-breadcrumb">
          <span>Dashboard</span>
          <span className="crumb-sep">/</span>
          <strong>Tickets</strong>
        </div>
        <div className="ticket-header-actions">
          <Button
            label="Workspace"
            icon="pi pi-comments"
            onClick={() => navigate("/tickets/workspace")}
            className="ghost-btn"
          />
          <Button
            label="Dashboard"
            icon="pi pi-chart-bar"
            onClick={() => navigate("/tickets/dashboard")}
            className="primary-btn"
          />
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            onClick={refreshTickets}
            className="ghost-btn"
          />
        </div>
      </div>

      <div className="ticket-filter-card">
        <div className="ticket-toolbar-left">
          <InputText
            value={filters.q}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, q: e.target.value, page: 0 }))
            }
            placeholder="Search tickets"
          />
          <Dropdown
            value={filters.status}
            options={statusOptions}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, status: e.value, page: 0 }))
            }
            placeholder="Status"
            showClear
          />
          <Dropdown
            value={filters.priority}
            options={priorityOptions}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, priority: e.value, page: 0 }))
            }
            placeholder="Priority"
            showClear
          />
          <Dropdown
            value={filters.assignedTo}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                assignedTo: e.value || "",
                page: 0,
              }))
            }
            options={TICKET_AGENT_OPTIONS}
            placeholder="Assigned To"
            showClear
          />
          <Calendar
            value={filters.from}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, from: e.value, page: 0 }))
            }
            placeholder="From"
            dateFormat="yy-mm-dd"
            showIcon
          />
          <Calendar
            value={filters.to}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, to: e.value, page: 0 }))
            }
            placeholder="To"
            dateFormat="yy-mm-dd"
            showIcon
          />
        </div>
      </div>

      <div className="ticket-table-card">
        <div className="ticket-table-header">
          <h3>All Tickets</h3>
          <span className="ticket-table-count">
            {filters.page * filters.limit + 1}-
            {Math.min(
              (filters.page + 1) * filters.limit,
              list.count || 0
            )}{" "}
            of {list.count || 0}
          </span>
        </div>
        <DataTable
          value={list.rows}
          paginator
          rows={filters.limit}
          totalRecords={list.count}
          onPage={onPage}
          first={filters.page * filters.limit}
          responsiveLayout="scroll"
          className="ticket-table"
          tableStyle={{ minWidth: "70rem" }}
        >
          <Column
            header="ID"
            body={(row) => `SKBT-${String(row.id).padStart(3, "0")}`}
          />
          <Column field="subject" header="Subject" />
          <Column field="name" header="Name" />
          <Column field="email" header="Email" />
          <Column header="Status" body={statusTag} />
          <Column header="Priority" body={priorityTag} />
          {/* <Column header="Assigned To" body={assignedBody} /> */}
        <Column
          header="Created At"
          body={(row) => formatDate(row.createdAt)}
        />
          <Column
            header="Updated At"
            body={(row) => formatDate(row.updatedAt)}
          />
          <Column header="Action" body={viewBody} />
        </DataTable>
      </div>
    </div>
  );
};
